import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Github, Mail, MapPin, ChevronDown, Sparkles, Eye } from 'lucide-react';
import { personalInfo as mockPersonalInfo, greetings } from '../data/mock';
import { useTheme } from '../context/ThemeContext';
import SpotifyNowPlaying from '../components/SpotifyNowPlaying';
import MouseFollower from '../components/MouseFollower';

const BACKEND_URL = '';

const HomePage = () => {
  const { themeColor, currentButtonShape } = useTheme();
  const [currentGreeting, setCurrentGreeting] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [visitorCount, setVisitorCount] = useState(0);
  const [timeGreeting, setTimeGreeting] = useState('');
  const [personalInfo, setPersonalInfo] = useState(mockPersonalInfo);
  const [presence, setPresence] = useState({ status: 'offline', label: 'Çevrim Dışı' });

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/public/settings`, { cache: 'no-store' })
      .then(r => r.json())
      .then(data => { if (data.personalInfo) setPersonalInfo(prev => ({ ...prev, ...data.personalInfo })); })
      .catch(() => {});
    // Presence polling
    const fetchPresence = () => {
      fetch(`${BACKEND_URL}/api/spotify/presence`, { cache: 'no-store' })
        .then(r => r.json())
        .then(d => setPresence(d))
        .catch(() => {});
    };
    fetchPresence();
    const presenceInterval = setInterval(fetchPresence, 15000);
    return () => clearInterval(presenceInterval);
  }, []);

  // Time-based greeting with icon
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 6) setTimeGreeting('İyi Geceler');
    else if (hour < 12) setTimeGreeting('Günaydın');
    else if (hour < 18) setTimeGreeting('İyi Günler');
    else if (hour < 22) setTimeGreeting('İyi Akşamlar');
    else setTimeGreeting('İyi Geceler');
  }, []);

  // Fetch visitor count
  useEffect(() => {
    const fetchVisitors = async () => {
      try {
        await fetch(`/api/visitors/increment`, { method: 'POST' });
        const response = await fetch(`/api/visitors`, { cache: 'no-store' });
        const data = await response.json();
        setVisitorCount(data.count || 0);
      } catch (_) {
        // network error - silently fail
      }
    };
    fetchVisitors();
  }, []);

  // Typing animation
  const typeText = useCallback((text, index = 0) => {
    if (index <= text.length) {
      setDisplayText(text.substring(0, index));
      setTimeout(() => typeText(text, index + 1), 80);
    } else {
      setIsTyping(false);
    }
  }, []);

  const deleteText = useCallback((text, index) => {
    if (index >= 0) {
      setDisplayText(text.substring(0, index));
      setTimeout(() => deleteText(text, index - 1), 40);
    } else {
      setIsTyping(true);
      setCurrentGreeting((prev) => (prev + 1) % greetings.length);
    }
  }, []);

  useEffect(() => {
    const greeting = greetings[currentGreeting];
    if (isTyping) {
      typeText(greeting.text);
    }
  }, [currentGreeting, isTyping, typeText]);

  useEffect(() => {
    if (!isTyping) {
      const timeout = setTimeout(() => {
        deleteText(greetings[currentGreeting].text, greetings[currentGreeting].text.length);
      }, 2500);
      return () => clearTimeout(timeout);
    }
  }, [isTyping, currentGreeting, deleteText]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 2.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Mouse Follower - Blurry color effect */}
      <MouseFollower />

      {/* Gradient Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-15 blur-[120px]"
          style={{ backgroundColor: themeColor.primary }}
          animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 20, repeat: Infinity }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full opacity-10 blur-[100px]"
          style={{ backgroundColor: themeColor.primary }}
          animate={{ scale: [1.2, 1, 1.2] }}
          transition={{ duration: 15, repeat: Infinity }}
        />
      </div>

      <div className="relative z-10 min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <motion.div
            className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Left Content */}
            <div className="order-2 lg:order-1">
              {/* Time Greeting Badge */}
              <motion.div
                variants={itemVariants}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-sm font-medium glass"
                style={{ color: themeColor.primary }}
              >
                <Sparkles size={16} />
                {timeGreeting}
              </motion.div>

              {/* Main Greeting */}
              <motion.div
                variants={itemVariants}
                className="relative mb-6"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                {/* Flag Tooltip */}
                <motion.div
                  className="absolute -top-12 left-0 px-4 py-2 rounded-xl bg-zinc-900/90 backdrop-blur-sm border border-zinc-800/50"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : 10 }}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{greetings[currentGreeting].flag}</span>
                    <span className="text-sm text-zinc-400">{greetings[currentGreeting].lang}</span>
                  </div>
                </motion.div>

                <div className="flex items-center gap-4">
                  <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold">
                    <span style={{ color: themeColor.primary }}>{displayText}</span>
                    <motion.span
                      className="inline-block w-1 h-16 md:h-20 ml-1"
                      style={{ backgroundColor: themeColor.primary }}
                      animate={{ opacity: [1, 0] }}
                      transition={{ duration: 0.5, repeat: Infinity }}
                    />
                  </h1>
                  <motion.img
                    src="https://raw.githubusercontent.com/MartinHeinz/MartinHeinz/master/wave.gif"
                    alt="wave"
                    className="w-16 h-16 md:w-20 md:h-20"
                    animate={{ rotate: [0, 15, -15, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
                  />
                </div>
              </motion.div>

              {/* Intro */}
              <motion.h2
                variants={itemVariants}
                className="text-2xl md:text-4xl font-bold text-white mb-4"
              >
                This is <span style={{ color: themeColor.primary }}>{personalInfo.name}</span> universe.
              </motion.h2>

              <motion.p
                variants={itemVariants}
                className="text-zinc-400 text-lg md:text-xl mb-8 max-w-xl leading-relaxed"
              >
                {personalInfo.bio}
              </motion.p>

              {/* CTA Buttons */}
              <motion.div variants={itemVariants} className="flex flex-wrap gap-4 mb-8">
                <Link
                  to="/projects"
                  className="btn-glow group flex items-center gap-2 px-6 py-3 font-medium text-black transition-all hover:pr-8 rounded-2xl"
                  style={{ backgroundColor: themeColor.primary, borderRadius: currentButtonShape.radius }}
                >
                  <span>Projelerimi Gör</span>
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contact"
                  className="flex items-center gap-2 px-6 py-3 font-medium text-white glass glass-hover transition-all rounded-2xl"
                  style={{ borderRadius: currentButtonShape.radius }}
                >
                  <Mail size={18} />
                  <span>İletişime Geç</span>
                </Link>
              </motion.div>

              {/* Stats */}
              <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2 text-zinc-500">
                  <MapPin size={16} style={{ color: themeColor.primary }} />
                  <span>{personalInfo.location}</span>
                </div>
                <div className="flex items-center gap-2 text-zinc-500">
                  <Eye size={16} style={{ color: themeColor.primary }} />
                  <span>{visitorCount.toLocaleString()} ziyaretçi</span>
                </div>
              </motion.div>
            </div>

            {/* Right - Avatar & Spotify */}
            <motion.div
              variants={itemVariants}
              className="order-1 lg:order-2 flex flex-col items-center gap-8"
            >
              {/* Avatar */}
              <div className="relative">
                <motion.div
                  className="absolute -inset-6 rounded-full opacity-30 blur-3xl"
                  style={{ backgroundColor: themeColor.primary }}
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
                <div
                  className="relative w-56 h-56 md:w-72 md:h-72 rounded-full p-1"
                  style={{ background: `linear-gradient(135deg, ${themeColor.primary}, transparent 50%, ${themeColor.primary})` }}
                >
                  <img
                    src={personalInfo.avatar}
                    alt={personalInfo.name}
                    className="w-full h-full rounded-full object-cover bg-zinc-900"
                  />
                </div>
                {/* Status Badge - Dynamic based on Spotify */}
                <motion.div
                  className={`absolute -bottom-2 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full glass text-sm font-medium flex items-center gap-2 ${
                    presence.status === 'online' ? 'text-green-400' :
                    presence.status === 'away' ? 'text-yellow-400' :
                    'text-zinc-500'
                  }`}
                  animate={presence.status === 'online' ? { scale: [1, 1.02, 1] } : {}}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <span className={`w-2 h-2 rounded-full ${
                    presence.status === 'online' ? 'bg-green-500 animate-pulse' :
                    presence.status === 'away' ? 'bg-yellow-500' :
                    'bg-zinc-600'
                  }`} />
                  {presence.label}
                </motion.div>
              </div>

              {/* Spotify */}
              <SpotifyNowPlaying />
            </motion.div>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3 }}
          >
            <motion.div
              className="flex flex-col items-center gap-2 text-zinc-500 cursor-pointer"
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
            >
              <span className="text-xs">Aşağı Kaydır</span>
              <ChevronDown size={20} />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
